"use client";

/**
 * Account Settings (/dashboard/settings) — twin of the reference's
 * src/app/dashboard/settings/page.tsx at main@b80b4874, parity-locked to
 * parity-capture/fixtures/settings.profile.txt.
 *
 * Built 2026-08-19 because the VIP rail links here and the route did not
 * exist, so the link 404'd. Of the rail's dead links it was the only real
 * FN gap — the seven /admin/* rows are omitted by ruling (see
 * app/(shell)/_components/VipAppSidebar.tsx), this one was missing.
 *
 * The password half MOUNTS the existing ChangePasswordForm rather than
 * re-inlining the reference's third password form. That form was built in
 * C4 and its own header has said since then that it was waiting for "the
 * dashboard settings screen it belongs on". This is that screen.
 *
 * WIRE
 *   GET   auth/profile/ -> measured (orgs/auth-profile/success.json).
 *   PATCH auth/profile/ -> verb+path measured, body a port of the
 *                          reference client, response unmeasured.
 *   POST  auth/change-password/ -> measured, both branches.
 * Details and the not-on-the-spine facts live on authApi.profile /
 * authApi.updateProfile.
 *
 * ORACLE BLIND SPOT — read before trusting a green parity run. The ruler
 * records CONTROLS, so everything below is invisible to it and is pinned
 * by TEXT-level assertions in ../parity/accountSettings.parity.test.tsx
 * instead: the page subtitle, the four profile field labels (unnamed in
 * the reference, so they are not even in the oracle's fingerprint), the
 * loading frame's text, both save-result messages, and the password
 * checklist rows. Nine real divergences got past a green suite in the
 * 2026-08-18 sweep for exactly this reason.
 *
 * DECLARED DIVERGENCES (complete list; D1 is the only oracle-visible one)
 *
 *  D1. The four Profile inputs are NAMED here (label htmlFor + input id).
 *      The reference's labels carry no htmlFor and its inputs no id, so
 *      all four have no accessible name — an a11y defect the oracle pins
 *      as not-to-inherit, exactly like the my-orders actions
 *      columnheader. Consequence for the comparison: the oracle DROPS
 *      unnamed controls from its fingerprint, so the twin renders four
 *      lines the oracle does not have, and the parity spec substitutes
 *      them in with an anchor assertion.
 *
 *  D2. The three password fields carry this app's PasswordInput, which
 *      adds a show/hide toggle — three buttons the reference's settings
 *      page does not render. Not an invention: the REFERENCE ships the
 *      same component (src/components/ui/PasswordInput.tsx, "#687 R2",
 *      identical toggle down to the "Show password" / "Hide password"
 *      labels) and uses it on login, register and reset-password. Its
 *      settings page is the one credential screen that misses it, so a
 *      VIP user has already met this affordance on that app's own
 *      screens. Regressing FN's shared primitive to a plain input for one
 *      screen would trade an accessibility affordance for a consistency
 *      the reference does not itself keep. Cheap to reverse if the owner
 *      disagrees: swap PasswordInput for Input in ChangePasswordForm.
 *
 *  D3. The Set Password branch is NOT BUILT. The reference switches the
 *      whole card to "Set Password" + POST auth/set-password/ when
 *      has_password is false. That endpoint has NO corpus fixture and is
 *      absent from the spine's OpenAPI schema, so there is no wire to
 *      build against and none to invent; the state is also unreachable on
 *      the measured corpus (both measured bodies carry has_password
 *      true), and the onboarding flows that produce such a user (QR /
 *      domain onboarding, access-requests) are not in FN either. The card
 *      renders the reference's own "Set Password" heading over an
 *      explicit unavailable notice rather than showing a Current Password
 *      field a user without a password cannot fill.
 *
 *  D4. A failed profile load is SURFACED. The reference swallows it
 *      (`.catch(() => {})`) and renders the form with empty fields, which
 *      reads as "your profile is blank" rather than "we could not read
 *      it". FN surfaces it, and it matters more here than in the
 *      reference: auth/profile/ is not on the spine at all today, so
 *      silently-empty fields would be the DEFAULT experience rather than
 *      an edge case. The fields stay empty as the reference's do — no
 *      substitute data is pulled from the auth user.
 *
 *  D5. The password card's SUCCESS message is the server's own
 *      ({ message } from the measured change-password body); the
 *      reference hardcodes "Password updated". Inherited from
 *      ChangePasswordForm, which renders what the server returns.
 *      Similarly the client-side mismatch message reads "New passwords do
 *      not match" against the reference's "Passwords do not match", and
 *      the too-short case is enforced by the input's minLength rather
 *      than the reference's own pre-submit string.
 *
 *  D6. The password checklist is the reference's MINUS EXACTLY TWO
 *      SENTENCES. Measured, not estimated: both pages were rendered from
 *      the same corpus body and their `textContent` diffed.
 *        "Common passwords are blocked on submit."
 *        "The server also checks breached-password lists before accepting
 *         your password."
 *      Both belong to the reference's <BreachRow>, dropped in C3 along with
 *      the third-party network dependency it needs and with no fixture to
 *      measure it (see modules/auth/lib/passwordPolicy.ts). Everything else
 *      on the two screens is byte-identical text.
 *
 *      The diff originally returned a THIRD sentence — the identity rule's
 *      sub-line, "Compared against the email, username, first name, and
 *      last name you entered." That one was an undeclared drop: the FN
 *      PasswordRequirements port had no `detail` support at all, so the
 *      explanation was missing from all four screens rendering that
 *      control, while its header claimed only the breach row was absent.
 *      Restored 2026-08-19 with `detail` on the shared component, since it
 *      has no dependency the breach row has. The parity spec now asserts
 *      it PRESENT so it cannot slip back into the declared-absence bucket.
 *
 * NOT captured by the oracle, so declared rather than oracle-enforced:
 * the loading frame, both alert states, and the Set Password branch.
 */
import { AlertCircle, CheckCircle } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";

import {
  authApi,
  AuthApiError,
  ChangePasswordForm,
  useAuth,
  type AuthUser,
} from "@/modules/auth";

type SaveMessage = { type: "success" | "error"; text: string };

/** The reference's own alert block, ported (role=alert for both tones). */
function ResultAlert({ message }: { message: SaveMessage }) {
  return (
    <div
      role="alert"
      className={`mb-4 flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${
        message.type === "success"
          ? "border border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-300"
          : "border border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300"
      }`}
    >
      {message.type === "success" ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
      {message.text}
    </div>
  );
}

const fieldClass =
  "w-full rounded-lg border border-edge px-3 py-2 text-sm outline-none focus:border-accent focus:ring-1 focus:ring-accent";
const labelClass = "mb-1 block text-sm font-medium text-ink-light";

export function AccountSettingsScreen() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  // D4: the reference swallows this; FN says so.
  const [loadError, setLoadError] = useState<string | null>(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [profileMsg, setProfileMsg] = useState<SaveMessage | null>(null);
  const [profileSaving, setProfileSaving] = useState(false);

  useEffect(() => {
    // `if (!user) return` — the D39 finding-9 remedy, same as
    // TeamOrdersScreen: without it the profile GET fires before the auth
    // bootstrap resolves, and a session whose access cookie is mid-refresh
    // 401s on first mount. `loading` already initializes to true, so the
    // screen simply stays in the reference's own loading frame until auth
    // lands. An anonymous visitor therefore never leaves it — route
    // guarding is src/middleware.ts's job, outside this module's lane.
    if (!user) return;
    let cancelled = false;
    authApi
      .profile()
      .then((p) => {
        if (cancelled) return;
        setProfile(p);
        setFirstName(p.first_name || "");
        setLastName(p.last_name || "");
        setEmail(p.email || "");
        setPhoneNumber(p.phone_number || "");
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(
          err instanceof AuthApiError
            ? (err.detail ?? "Could not load your profile.")
            : err instanceof Error
              ? err.message
              : "Could not load your profile.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  async function handleProfileSave(event: FormEvent) {
    event.preventDefault();
    setProfileSaving(true);
    setProfileMsg(null);
    try {
      const updated = await authApi.updateProfile({
        first_name: firstName,
        last_name: lastName,
        email,
        phone_number: phoneNumber,
      });
      setProfile(updated);
      setProfileMsg({ type: "success", text: "Profile updated" });
    } catch (err) {
      setProfileMsg({
        type: "error",
        text:
          err instanceof AuthApiError
            ? (err.detail ?? "Failed to update profile")
            : err instanceof Error
              ? err.message
              : "Failed to update profile",
      });
    } finally {
      setProfileSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="p-8">
        <p className="text-sm text-ink-faint">Loading...</p>
      </div>
    );
  }

  // The reference's own fallback ladder: the profile body, then the auth
  // user, then true. Both measured bodies carry has_password true.
  const hasPassword = profile?.has_password ?? user?.has_password ?? true;

  return (
    <div className="max-w-2xl p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-ink">Account Settings</h1>
        <p className="mt-1 text-sm text-ink-light">
          Manage your profile and security settings.
        </p>
      </div>

      {/* Profile */}
      <div className="ds-card ds-radius-card mb-6 p-6">
        <h2 className="mb-4 text-base font-semibold text-ink">Profile</h2>
        {loadError && <ResultAlert message={{ type: "error", text: loadError }} />}
        {profileMsg && <ResultAlert message={profileMsg} />}
        <form onSubmit={handleProfileSave} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              {/* D1: named, unlike the reference's four bare labels. */}
              <label htmlFor="settings-first-name" className={labelClass}>
                First Name
              </label>
              <input
                id="settings-first-name"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className={fieldClass}
              />
            </div>
            <div>
              <label htmlFor="settings-last-name" className={labelClass}>
                Last Name
              </label>
              <input
                id="settings-last-name"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className={fieldClass}
              />
            </div>
          </div>
          <div>
            <label htmlFor="settings-email" className={labelClass}>
              Email
            </label>
            <input
              id="settings-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={fieldClass}
            />
          </div>
          <div>
            <label htmlFor="settings-phone-number" className={labelClass}>
              Phone Number
            </label>
            <input
              id="settings-phone-number"
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className={fieldClass}
            />
          </div>
          <button
            type="submit"
            disabled={profileSaving}
            className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-on-accent transition-colors hover:brightness-[0.93] disabled:opacity-50"
          >
            {profileSaving ? "Saving..." : "Save Changes"}
          </button>
        </form>
      </div>

      {/* Password */}
      <div className="ds-card ds-radius-card p-6">
        <h2 className="mb-4 text-base font-semibold text-ink">
          {hasPassword ? "Change Password" : "Set Password"}
        </h2>
        {hasPassword ? (
          <ChangePasswordForm />
        ) : (
          // D3: no set-password wire exists to build against, in the corpus
          // or on the spine. An honest dead end beats a Current Password
          // field a user without a password cannot fill.
          <div role="status" className="space-y-1 text-sm text-ink-light">
            <p>Setting your first password isn&apos;t available here yet.</p>
            <p className="text-xs text-ink-faint">
              This screen can only change an existing password.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
